import React from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';

import config from 'config';

import { CmsEntry, ICmsEntry } from '..';
import { getLocale } from '@library/locale';
import { cmsEntry } from '../CmsEntry';
import { ILocatedProps, ILocatedPropsAsObject } from '../..';
import { IMappedData } from '../../types';

const trailingSlash = /\/$/;

export const LanguageTags = (props: Omit<ILocatedProps, 'locale'>): JSX.Element => {
  const renderLangTags = (): JSX.Element[] => {
    const host = config.apiHost.replace(/\/$/, '');
    return Object.keys(props).map(language => {
      const locale = getLocale(language);
      const slug =
        (props[locale.withCountryCode] &&
          (props[locale.withCountryCode] as Record<string, unknown>).props &&
          ((props[locale.withCountryCode] as Record<string, unknown>).props as Record<string, unknown>).slug) ||
        undefined;
      if (!slug) {
        return <></>;
      }
      const href = `${host}${locale.pathPrefix}${slug}`.replace(trailingSlash, '');
      return <link key={locale.country} rel="alternate" hrefLang={locale.locale} href={href} />;
    });
  };
  return <Head>{renderLangTags()}</Head>;
};

export const LocatedPropsWithHooks = ({ locale, ...props }: ILocatedProps): JSX.Element => {
  const router = useRouter();
  const pageInitialLanguage = getLocale(router.locale || locale).withCountryCode;
  const pageProps = props[pageInitialLanguage] as ICmsEntry;

  return (
    <>
      <LanguageTags {...props} />
      {pageProps ? <CmsEntry {...pageProps} /> : null}
    </>
  );
};

type PageProps = {
  children: IMappedData[];
};

export const locatedPropsWithHooksAsObject = <Content = unknown,>(
  { locale, ...props }: ILocatedProps,
  { localeFromRouter }: { localeFromRouter: string | undefined },
): ILocatedPropsAsObject<Content> => {
  const pageInitialLanguage = getLocale(localeFromRouter || locale).withCountryCode;
  const pageProps = props[pageInitialLanguage] as ICmsEntry;
  const seoTagsProps = (pageProps.props as PageProps).children[0].props;

  return {
    languageTags: props,
    content: cmsEntry<Content>({ ...pageProps }).content,
    seoTags: seoTagsProps,
  };
};
